import React from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';

const WallDetailGraph = ({ proposedGrades = [] }) => {
  const theme = useTheme();

  // Funzione per convertire i gradi di arrampicata in valori numerici
  const gradeToNumber = (grade) => {
    if (!grade) return 0;
    
    const gradeStr = grade.toString().toLowerCase();
    
    // Mappa dei gradi francesi a valori numerici
    const gradeMap = {
        '4a': 4.0, '4a+': 4.2, '4b': 4.4, '4b+': 4.6, '4c': 4.8, '4c+': 5.0,
        '5a': 5.2, '5a+': 5.4, '5b': 5.6, '5b+': 5.8, '5c': 6.0, '5c+': 6.2,
        '6a': 6.4, '6a+': 6.6, '6b': 6.8, '6b+': 7.0, '6c': 7.2, '6c+': 7.4,
        '7a': 7.6, '7a+': 7.8, '7b': 8.0, '7b+': 8.2, '7c': 8.4, '7c+': 8.6,
        '8a': 8.8, '8a+': 9.0, '8b': 9.2, '8b+': 9.4, '8c': 9.6, '8c+': 9.8,
        '9a': 10.0
    };
    
    return gradeMap[gradeStr] || 0;
  };

  // Funzione per ottenere tutti i gradi unici per le etichette Y
  const getUniqueGrades = (grades) => {
    const uniqueGrades = [...new Set(grades.map(g => g.grade).filter(Boolean))];
    return uniqueGrades.sort((a, b) => gradeToNumber(a) - gradeToNumber(b));
  };

  // Trasforma i dati per il grafico
  const rawData = proposedGrades
    .map((grade, index) => ({
      date: dayjs(grade.updatedAt).format('DD-MMM-YY'),
      fullDate: grade.updatedAt,
      dateNumber: new Date(grade.updatedAt).getTime(), // Valore numerico per l'asse X
      grade: grade.grade,
      gradeNumber: gradeToNumber(grade.grade),
      rating: grade.rating,
      user: grade.userId ? `${grade.userId.name} ${grade.userId.surname}` : 'N/A',
      notes: grade.notes,
      timestamp: new Date(grade.updatedAt).getTime(),
    }))
    .sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));

  // Usa tutti i dati senza raggruppamento - un punto per ogni grado proposto
  const chartData = rawData.map((item, index) => ({
    ...item,
    xOffset: 0,
    isMultiple: false, // Ogni punto rappresenta un singolo contributo
  }));

  const uniqueGrades = getUniqueGrades(rawData);

  // Se non ci sono dati, mostra un messaggio
  if (chartData.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Gradi Proposti
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Nessun grado proposto disponibile per visualizzare il grafico.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Gradi Proposti
      </Typography>
      <Box sx={{ width: '100%', height: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis 
              dataKey="dateNumber"
              type="number"
              scale="time"
              stroke={theme.palette.text.secondary}
              fontSize={12}
              tickFormatter={(value) => {
                return dayjs(value).format('DD-MMM-YY');
              }}
              domain={['dataMin', 'dataMax']}
            />
            <YAxis 
              dataKey="gradeNumber"
              stroke={theme.palette.text.secondary}
              fontSize={12}
              // label={{ value: 'Grado Proposto', angle: -90, position: 'insideLeft' }}
              domain={['dataMin - 0.5', 'dataMax + 0.5']}
              tickFormatter={(value) => {
                // Trova il grado originale corrispondente al valore numerico
                const originalGrade = uniqueGrades.find(grade => 
                  Math.abs(gradeToNumber(grade) - value) < 0.1
                );
                return originalGrade || value.toString();
              }}
              ticks={uniqueGrades.map(grade => gradeToNumber(grade))}
            />
            <Scatter
              data={chartData}
              fill={theme.palette.primary.main}
              shape={(props) => {
                const { cx, cy, payload } = props;
                if (!payload) return null;
                
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={4}
                    fill={theme.palette.primary.main}
                    stroke={theme.palette.background.paper}
                    strokeWidth={2}
                  />
                );
              }}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </Box>
      {/* <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        Ogni punto rappresenta un grado proposto. I punti sovrapposti indicano più gradi proposti nello stesso giorno.
      </Typography> */}
    </Paper>
  );
};

export default WallDetailGraph;
